import { ConfigProvider, Modal } from 'antd';

import type { CleaningRequirement } from '../../interfaces/ProcessCatalog.Interface';
import { THEME } from '../../main';

/**
 * PTB-6441: a static `Modal.confirm()` mounts outside the ConfigProvider tree,
 * so Tailwind's preflight strips the primary button's styles and Yes renders
 * invisible until hovered. Same fix as the sample-labeling confirmations.
 */
const themedFooter = (
	_originNode: React.ReactNode,
	{ OkBtn, CancelBtn }: { OkBtn: React.FC; CancelBtn: React.FC }
) => (
	<ConfigProvider theme={THEME}>
		<CancelBtn />
		<OkBtn />
	</ConfigProvider>
);

/**
 * Asked once per delivery, when the job's cleaning is not the standard one.
 *
 * No PIN: the operator is already identified on the delivery form, and the
 * question is a reminder rather than an authorisation. Cancel resolves `false`
 * — a dismissed dialog is an unanswered question — and the caller must abandon
 * the delivery, which is also what the server does if the confirmation is
 * missing from the payload.
 */
export function confirmCleaning(cleaning: CleaningRequirement): Promise<boolean> {
	return new Promise((resolve) => {
		Modal.confirm({
			centered: true,
			width: 560,
			title: `This job requires ${cleaning.name} cleaning`,
			content: (
				<div style={{ fontSize: 16, marginTop: 12 }}>
					{cleaning.instructions && (
						<p style={{ whiteSpace: 'pre-wrap' }}>{cleaning.instructions}</p>
					)}
					<p style={{ marginBottom: 0 }}>
						Were these parts cleaned this way? The delivery tag will not print
						until you confirm.
					</p>
				</div>
			),
			okText: 'Yes, parts were cleaned',
			cancelText: 'Cancel',
			footer: themedFooter,
			onOk: () => resolve(true),
			onCancel: () => resolve(false),
		});
	});
}
